<template>
  <with-loader class="flex flex-col" id="conversations-list" :loading="isLoading" use-as-overlay>
    <span v-if="!hasConversations" class="p-4">
      No conversations found in the specified date range.
    </span>
    <side-list
      id="test-conversations-list"
      ref="conversations"
      :items="conversations"
      :selected-id="contactId"
      id-key="contact_id"
      @item-selected="$emit('select-conversation', $event)"
      @scroll="handleScroll"
    >
      <template #item="{item, isItemSelected}">
        <router-link :to="{ name: ROUTE_NAMES.history, query: { ...$route.query, contactId: item.contact_id } }"
                     class="px-3 py-4 block"
        >
          <div class="flex justify-between">
            <div class="flex">
              <span v-for="(legendType, i) of mapToLegendTypes(item.outcomes)" :key="i">
                <chart-legend :type="legendType" :data-cy="legendType" :show-label="false" class="mr-1"/>
              </span>
            </div>
            <span class="flex text-sm text-gray-dark">
              <span>{{ formatTimestamp(item.last_request_timestamp, 'DD MMM') }}</span>
              <span class="hidden md:block md:ml-1">
                {{ formatTimestamp(item.last_request_timestamp, 'HH:mm') }}
              </span>
            </span>
          </div>

          <div class="clamp font-medium text-lg mt-2 text-body"
               :class="{ 'text-black': isItemSelected }"
               :title="item.initial_request"
          >
            {{ stripHtmlTags(item.initial_request) }}
          </div>

          <div class="test-conversations-list-contact-id mt-1 text-sm text-gray-dark">
            ID: {{ item.contact_id }}
          </div>
        </router-link>
      </template>
    </side-list>
  </with-loader>

</template>

<script>
import ChartLegend from '../charts/ChartLegend.vue'
import SideList from '@/components/common/SideList.vue'
import isEmpty from 'ramda/src/isEmpty'
import moment from 'moment'
import requestOutcomeMapper from '@/utils/request-outcome.mapper'
import { ROUTE_NAMES } from '@/router'
import WithLoader from '@/components/common/WithLoader.vue'

export default {
  name: 'ConversationsList',
  components: { WithLoader, ChartLegend, SideList },

  props: {
    conversations: {
      type: Array,
      required: true
    },
    hasNext: {
      type: Boolean,
      default: false
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    contactId: {
      type: String,
      default: ''
    },
    wasNavigationClicked: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      ROUTE_NAMES,
      wasScrolled: false
    }
  },

  computed: {
    conversationsEl() {
      return this.$refs.conversations?.$el
    },

    selectedConversationEl() {
      // to get side-list-item element, adding ref prop to router-link inside slot is causing
      // list to scroll to wrong position
      return this.conversationsEl?.querySelector(
        `#contact_id${this.contactId}`
      )
    }
  },

  watch: {
    isLoading: {
      immediate: false,
      async handler(isLoading) {
        // wasScrolled to prevent scrolling in case conversations loaded by user's scrolling
        if (isLoading || this.wasScrolled) return

        await this.scrollToSelectedConversation(true)
      }
    },

    contactId: {
      immediate: false,
      async handler() {
        if (!this.wasNavigationClicked) return

        this.handleNavigationMovement()
      }
    }
  },

  methods: {
    formatTimestamp(timestamp, format) {
      return moment(timestamp).format(format)
    },

    handleScroll({ target }) {
      const { scrollHeight, scrollTop, clientHeight } = target
      const isNearBottom = scrollHeight - (scrollTop + 100) <= clientHeight
      if (isNearBottom && this.hasNext) {
        this.$emit('load-conversations')
      }

      this.wasScrolled = true
    },

    handleNavigationMovement() {
      const { clientHeight } = this.conversationsEl
      const bounding = this.selectedConversationEl.getBoundingClientRect()
      if (bounding.top >= clientHeight) {
        this.scrollToSelectedConversation(false)
      } else if (bounding.top - this.selectedConversationEl.clientHeight <= 0) {
        this.scrollToSelectedConversation(true)
      }
    },

    hasConversations() {
      return !isEmpty(this.conversations)
    },

    mapToLegendTypes(outcomes) {
      const legendTypes = outcomes.map(outcome => requestOutcomeMapper.mapOutcomeToLegend(outcome))
      return [...new Set(legendTypes)]
    },

    async scrollToSelectedConversation(scrollToTop) {
      await this.$nextTick()
      if (!this.selectedConversationEl) return

      this.selectedConversationEl?.scrollIntoView(scrollToTop)
    }
  }
}
</script>
